import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import { createCustomer, clearCreateError, fetchInvoiceEditData } from '../../../features/customer/customer.slice';
import { useSelector } from 'react-redux';
import type { CustomerFormData, ValidationErrors, AddCustomerModalProps } from '../types';
import countries from 'countries-list';
import {
    validateCurrentStep,
    validateVatNumber as validateVatNumberUtil
} from '../validations';
import BusinessInformation from './BusinessInformation';
import ContactInformation from './ContactInformation';
import Preferences from './Preferences';
import StepIndicator from './StepIndicator';
import { getDefaultFormState } from '../helpers/modalHelper';
import '../styles/add-customer-modal.scss';
import { handleGLNInput, handleNumberOnlyInput, handlePhoneNumberInput, handleSSNInput, handleSSNKeyDown, handleVatNumberInput, handleVatNumberKeyDown, handleZipCodeInput } from '../helpers/input-restriction.helper';
import { validateVatNumberApi } from '../../../api/customer';
import { europeanVatCountryCodes } from '../../../../common/StringUtils';
import { getCurrentTourStepKey, subscribeToTourStepKey } from '../../tour/tour-step-state';

interface AddCustomerOnlyModalProps extends Omit<AddCustomerModalProps, 'initialData' | 'isEdit'> {
    show: boolean;
    onHide: () => void;
    onSave: (customerData: CustomerFormData) => void;
}
const AddCustomerModal: React.FC<AddCustomerOnlyModalProps> = ({
    show,
    onHide,
    onSave,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { createLoading, createError, updateLoading, updateError } = useAppSelector((state: any) => state?.customer || {});
    const userID = useSelector((state: any) => state?.auth?.user?.userId);
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [errors, setErrors] = useState<ValidationErrors>({});
    const [vatNumberError, setVatNumberError] = useState<string>('');
    const [isVatValidating, setIsVatValidating] = useState<boolean>(false);
    const [vatApiValidationStatus, setVatApiValidationStatus] = useState<'valid' | 'invalid' | 'error' | null>(null);
    const lastValidatedVatRef = React.useRef<string>('');
    const [refreshLoading, setRefreshLoading] = useState<boolean>(false);
    const [formData, setFormData] = useState<CustomerFormData>(getDefaultFormState());

    /* Guided tour lock: the modal must ONLY be locked when the tour is
       genuinely on its `add-customer-modal` step. If a user opens this
       modal manually OUTSIDE the tour (e.g. from anywhere else in the
       app), the normal close paths must keep working.
       Previously we listened for the `add-customer-modal-opened` event
       — which fires on every modal open — and that incorrectly locked
       the modal in non-tour scenarios too. Switching to the live
       step-key store ties the lock directly to the tour's actual step,
       not to a one-shot event. */
    const [tourLocked, setTourLocked] = useState<boolean>(false);
    useEffect(() => {
        const sync = () => setTourLocked(getCurrentTourStepKey() === 'add-customer-modal');
        sync(); // initial — in case the tour is already on the step when we mount
        return subscribeToTourStepKey(sync);
    }, []);

    useEffect(() => {
        if (show) {
            resetForm();
        }
    }, [show]);

    // Auto-set isVATRegistered based on business type and country on mount and changes
    useEffect(() => {
        if ((formData.businessType === '2' || formData.businessType === '3') && formData.country === 'Sweden') {
            if (formData.isVATRegistered !== 'true') {
                setFormData(prev => ({
                    ...prev,
                    isVATRegistered: 'true'
                }));
            }
        }
    }, [formData.businessType, formData.country]);

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => {
            // Handle boolean checkbox fields
            let processedValue: any = value;
            if (field === 'useExistingEmail' || field === 'useExistingAddress') {
                processedValue = value === 'true';
            }
            
            const newFormData = {
                ...prev,
                [field]: processedValue
            };
            // Clear secondaryEmail when useExistingEmail is checked
            if (field === 'useExistingEmail' && processedValue === true) {
                newFormData.secondaryEmail = '';
            }

            // Clear postal address fields when useExistingAddress is checked
            if (field === 'useExistingAddress' && processedValue === true) {
                newFormData.postAddress = '';
                newFormData.postCity = '';
                newFormData.postZipCode = '';
                newFormData.postCountry = '';
                newFormData.postCountryCode = '';
            }
            // Auto-set isVATRegistered to "true" when business type is 2 or 3 AND country is Sweden
            if (field === 'country' || field === 'businessType') {
                const businessType = field === 'businessType' ? value : prev.businessType;
                const country = field === 'country' ? value : prev.country;
                
                if ((businessType === '2' || businessType === '3') && country === 'Sweden') {
                    newFormData.isVATRegistered = 'true';
                }
            }

            return newFormData;
        });

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }

        // Clear VAT number error and API validation status when VAT country code or VAT number changes
        if (field === 'vatCountryCode' || field === 'vatNumber') {
            setVatNumberError('');
            setVatApiValidationStatus(null);
            lastValidatedVatRef.current = '';
        }

        // Auto-update VAT country code when country changes
        if (field === 'country') {
            let countryCode = '';
            
            // Map country name to country code using countries-list
            Object.keys(countries.countries).forEach((code) => {
                if ((countries.countries as any)[code].name === value) {
                    countryCode = code;
                }
            });
            
            // Special case for USA
            if (value === 'USA') {
                countryCode = 'US';
            }
            
            // Update vatCountryCode if a valid code was found
            if (countryCode) {
                setFormData(prev => ({
                    ...prev,
                    [field]: value,
                    vatCountryCode: countryCode
                }));
            }
        }
        
        // Auto-update postal country code when postal country changes
        if (field === 'postCountry') {
            let countryCode = '';
            
            // Map country name to country code using countries-list
            Object.keys(countries.countries).forEach((code) => {
                if ((countries.countries as any)[code].name === value) {
                    countryCode = code;
                }
            });
            
            // Special case for USA
            if (value === 'USA') {
                countryCode = 'US';
            }
            
            // Update postCountryCode if a valid code was found
            if (countryCode) {
                setFormData(prev => ({
                    ...prev,
                    [field]: value,
                    postCountryCode: countryCode
                }));
            }
        }
    };

    const validateVatNumberWithError = (vatCountryCode: string, vatNumber: string): boolean => {
        return validateVatNumberUtil(vatCountryCode, vatNumber, setVatNumberError, t);
    };

    const validateCurrentStepForm = (): boolean => {
        const { errors: newErrors, isValid } = validateCurrentStep(
            currentStep,
            formData,
            validateVatNumberWithError
        );

        setErrors(newErrors);
        return isValid;
    };

    const transformFormDataToAPI = useCallback((data: CustomerFormData) => {
        let concatVatNumber = '';
        if (data.isVATRegistered !== 'false' && data.hasVATNumber === 'true' && data.vatNumber) {
            // Check if vatNumber already starts with country code to prevent duplication (e.g., RSRS)
            if (data.vatNumber.toUpperCase().startsWith(data.vatCountryCode.toUpperCase())) {
                // Already has country code, use as-is
                concatVatNumber = data.vatNumber;
                 } else {
                // Add country code
                concatVatNumber = data.vatCountryCode + data.vatNumber;
                }
        }
        const apiData = {
            name: data.customerName,
            businessType: data.businessType,
            accountType: data.businessType,
            orgNo: data.orgNo,
            ssn: data.ssn,
            country: data.country,
            city: data.city,
            zipCode: data.zipCode,
            address: data.addressLine1,
            address2: data.addressLine2,
            addressLine1: data.addressLine1,
            addressLine2: data.addressLine2,
            hasVATNumber: data.hasVATNumber,
            ...(data.isVATRegistered !== 'false' && {
                vatNumber: concatVatNumber || data.vatNumber,
                vatCountryCode: data.vatCountryCode,
            }),
            yourElectronicReference: data.ourElectronicReference,
            globalLocationNumber: data.globalLocationNumber,
            extraField1: data.extraField1,
            extraField2: data.extraField2,
            co: data.careOf,
            invoiceCurrency: data.invoiceCurrency,
            language: (() => {
                const lang = data.invoiceLanguage || '';
                const lowerLang = lang.toLowerCase();
                console.log('🔍 [AddCustomerModal] Language Debug:');
                console.log('   Raw invoiceLanguage:', data.invoiceLanguage);
                console.log('   Lowercase:', lowerLang);
                
                // Handle old format (Swedish/English) and new format (sv-SE/en-US)
                let finalLang = 'en-us'; // default
                if (lowerLang === 'swedish' || lowerLang === 'sv-se') {
                    finalLang = 'sv-se';
                } else if (lowerLang === 'english' || lowerLang === 'en-us') {
                    finalLang = 'en-us';
                } else if (lowerLang) {
                    finalLang = lowerLang;
                }
                
                console.log('   Final language sent to API:', finalLang);
                return finalLang;
            })(),
            vatDetails: data.vatDetails,
            email: data.contactEmail || '',
            ccEmail: data.ccEmail || '',
            countryCode: data.isVATRegistered !== 'false' ? (data.vatCountryCode || 'SE') : 'SE', // Default to SE (Sweden)
            paymentTerms: data.paymentTerms || '30', // Default to 30 days
            id: data.id,
            invoiceType: data.invoiceDeliveryMethod || '1',
            reference: data?.reference,
            phoneNumber: data?.phoneNumber || '',
            // API expects 'isVATRegistred' (with 'd'), convert string to boolean
            isVATRegistred: data?.isVATRegistered === 'true',
            isVatVerified: data?.isVatVerified ?? false,
            // Secondary email - only send if useExistingEmail is false (user unchecked the box)
            ...(data.invoiceDeliveryMethod === '1' && !data.useExistingEmail && data.secondaryEmail && {
                secondaryEmailAddress: data.secondaryEmail,
                isSecondaryEmailAddress: true,
            }),
            // Set isSecondaryEmailAddress to false when using existing email
            ...(data.invoiceDeliveryMethod === '1' && data.useExistingEmail && {
                isSecondaryEmailAddress: false,
            }),
            // Postal delivery address fields - only send if useExistingAddress is false
            ...(data.invoiceDeliveryMethod === '3' && !data.useExistingAddress && {
                postAddress: data.postAddress || '',
                postCity: data.postCity || '',
                postCountryCode: data.postCountryCode || '',
                postZipCode: data.postZipCode || '',
                postCountry: data.postCountry || '',
            }),

        };
  return apiData;
    }, []);

    const handleNext = async () => {
        console.log({ currentStep });
        if (!validateCurrentStepForm()) {
            return; 
        }

        // If VAT is filled and not yet verified, call the API before proceeding.
        // A customer cannot be created (or moved past this step) with a VAT
        // number that fails verification - block progression and surface a
        // clear validation message instead of just warning.
        if (
            currentStep === 1 &&
            formData.isVATRegistered === 'true' &&
            formData.vatNumber?.trim() &&
            europeanVatCountryCodes.has(formData.vatCountryCode)
        ) {
            const status = await handleVatBlur(formData.vatNumber.trim());
            if (status === 'invalid') {
                setVatNumberError('VAT_VERIFICATION_REQUIRED_TO_CONTINUE');
                return;
            }
        }

        if (currentStep < 3) {
            setCurrentStep(currentStep + 1);
        } else {
            try {
                const customerData = transformFormDataToAPI(formData);
                const result = await dispatch(createCustomer(customerData));
                
                if (createCustomer.fulfilled.match(result)) {
                    
                    // Refresh customer data after successful creation
                    if (userID) {
                        try {
                            setRefreshLoading(true);
                            await dispatch(fetchInvoiceEditData(userID));
                        } finally {
                            setRefreshLoading(false);
                        }
                    }
                    
                    // Pass the created customer ID to parent for auto-selection
                    // The API returns the customer in result.payload
                    const createdCustomer = result.payload;
                    const createdCustomerId = createdCustomer?.customerId || createdCustomer?.id;
                    
                    onSave({ ...formData, customerId: createdCustomerId });
                    onHide();
                    resetForm();
                } else if (createCustomer.rejected.match(result)) {
                    // Error toast is already shown in the thunk, no need to show another one
                    console.error('Failed to create customer:', result.payload || result.error);
                }
            } catch (error) {
                console.error('Error creating customer:', error);
                toast.error(t('FAILED_TO_SAVE_CUSTOMER') as string);
            }
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleCancel = () => {
        /* When the tour is on the modal step, swallow every close
           request (X icon, backdrop click, Cancel button) until the
           user either creates a client or dismisses the tour itself. */
        if (tourLocked) return;
        dispatch(clearCreateError());
        resetForm();
        onHide();
    };

    const resetForm = () => {
        setCurrentStep(1);
        setErrors({});
        setVatNumberError('');
        setIsVatValidating(false);
        setVatApiValidationStatus(null);
        lastValidatedVatRef.current = '';
        setFormData(getDefaultFormState());
    };



    const handleVatBlur = async (vatNumber: string): Promise<'valid' | 'invalid' | 'error' | null> => {
        const fullVatNumber = vatNumber?.trim();
        if (!fullVatNumber) return null;
        if (fullVatNumber === lastValidatedVatRef.current) return vatApiValidationStatus ?? null;
        lastValidatedVatRef.current = fullVatNumber;
        setIsVatValidating(true);
        setVatApiValidationStatus(null);
        try {
            const result = await validateVatNumberApi(fullVatNumber);
            const { isValid, registrationData } = result.response;
            const status: 'valid' | 'invalid' = isValid ? 'valid' : 'invalid';
            setVatApiValidationStatus(status);
            setFormData(prev => {
                const updates: Partial<typeof prev> = { isVatVerified: isValid };
                if (isValid && registrationData) {
                    if (!prev.customerName && registrationData.name) updates.customerName = registrationData.name;
                    if (!prev.addressLine1 && registrationData.address) updates.addressLine1 = registrationData.address;
                    if (!prev.city && registrationData.city) updates.city = registrationData.city;
                    if (!prev.zipCode && registrationData.zip) {
                        // Strip non-digit characters (e.g. "LV-1012" → "1012")
                        updates.zipCode = registrationData.zip.replace(/\D/g, '').slice(0, 6);
                    }
                    if (!prev.country && registrationData.countryName) updates.country = registrationData.countryName;
                    if (!prev.vatCountryCode && registrationData.countryIso) updates.vatCountryCode = registrationData.countryIso;
                }
                return { ...prev, ...updates };
            });
            return status;
        } catch {
            setVatApiValidationStatus('error');
            return 'error';
        } finally {
            setIsVatValidating(false);
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <BusinessInformation
                        formData={formData}
                        errors={errors}
                        vatNumberError={vatNumberError}
                        setVatNumberError={setVatNumberError}
                        onInputChange={handleInputChange}
                        onNumberOnlyInput={handleNumberOnlyInput}
                        onSSNInput={handleSSNInput}
                        onSSNKeyDown={handleSSNKeyDown}
                        onVatNumberInput={(e) => handleVatNumberInput(e, formData)}
                        onVatNumberKeyDown={(e) => handleVatNumberKeyDown(e, formData)}
                        onZipCodeInput={handleZipCodeInput}
                        isVatValidating={isVatValidating}
                        vatApiValidationStatus={vatApiValidationStatus}
                        onVatBlur={(vatNumber) => {
                            if (europeanVatCountryCodes.has(formData.vatCountryCode)) {
                                handleVatBlur(vatNumber);
                            }
                        }}
                    />
                );
            case 2:
                return (
                    <ContactInformation
                        formData={formData}
                        errors={errors}
                        onInputChange={handleInputChange}
                        onPhoneNumberInput={(e) => handlePhoneNumberInput(e)}
                        onGLNInput={(e) => handleGLNInput(e)}
                    />
                );
            case 3:
                return (
                    <Preferences
                        formData={formData}
                        errors={errors}
                        onInputChange={handleInputChange}
                    />
                );
            default:
                return (
                    <BusinessInformation
                        formData={formData}
                        errors={errors}
                        vatNumberError={vatNumberError}
                        setVatNumberError={setVatNumberError}
                        onInputChange={handleInputChange}
                        onNumberOnlyInput={handleNumberOnlyInput}
                        onSSNInput={handleSSNInput}
                        onSSNKeyDown={handleSSNKeyDown}
                        onVatNumberInput={(e) => handleVatNumberInput(e, formData)}
                        onVatNumberKeyDown={(e) => handleVatNumberKeyDown(e, formData)}
                        onZipCodeInput={handleZipCodeInput}
                        isVatValidating={isVatValidating}
                        vatApiValidationStatus={vatApiValidationStatus}
                        onVatBlur={(vatNumber) => {
                            if (europeanVatCountryCodes.has(formData.vatCountryCode)) {
                                handleVatBlur(vatNumber);
                            }
                        }}
                    />
                );
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleCancel}
            size="xl"
            className="add-customer-modal"
            centered
            /* While the guided tour is on the modal step, lock down every
               escape hatch the Bootstrap Modal exposes by default:
                 • `backdrop="static"` — clicking outside no longer fires
                   onHide, so the modal stays put.
                 • `keyboard={false}` — Escape key no longer triggers onHide.
               Outside the tour these props revert to their defaults
               (`backdrop: true`, `keyboard: true`) so closing the modal
               by clicking outside or pressing Escape still works as
               expected. */
            backdrop={tourLocked ? 'static' : true}
            keyboard={!tourLocked}
        >
            <Modal.Header className="custom-modal-header">
                <Modal.Title>{t('ADD_CUSTOMER_MODAL_TITLE')}</Modal.Title>
                {/* X icon — hidden during the tour's modal step so the
                    user has only one exit: completing the form. */}
                {!tourLocked && (
                    <Button
                        variant="link"
                        className="close-btn"
                        onClick={handleCancel}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </Button>
                )}
            </Modal.Header>

            <Modal.Body className="custom-modal-body">
                <StepIndicator currentStep={currentStep} />
                <div className="step-divider-full"></div>
                
                {(createError) && (
                    <div className="alert alert-danger mb-3" role="alert">
                        {createError}
                    </div>
                )}

                {/* key={currentStep} re-triggers the slide/fade animation on Next/Back */}
                <div className="acm-step" key={currentStep}>
                    {renderStepContent()}
                </div>
            </Modal.Body>

            <Modal.Footer className="custom-modal-footer">
                {currentStep === 1 ? (
                    <>
                        {/* Cancel button — hidden while the tour is on
                            the modal step. Only exit is completing the
                            form (which auto-closes via parent's onSave). */}
                        {!tourLocked && (
                            <Button
                                variant="outline-primary"
                                className="cancel-btn"
                                onClick={handleCancel}
                            >
                                {t('CANCEL')}
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            className="next-btn"
                            onClick={handleNext}
                        >
                            {t('NEXT')}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            variant="outline-primary"
                            className="cancel-btn back-btn"
                            onClick={handleBack}
                        >
                            {t('BACK')}
                        </Button>
                        <div className="d-flex gap-3">
                            {/* Cancel button hidden during the tour's
                                modal step (Steps 2 / 3 of the inner
                                stepper). The form's progress within the
                                modal is preserved as the user navigates
                                via Back/Next, but they can only exit by
                                completing it. */}
                            {!tourLocked && (
                                <Button
                                    variant="outline-secondary"
                                    className="cancel-btn"
                                    onClick={handleCancel}
                                >
                                    {t('CANCEL')}
                                </Button>
                            )}
                            <Button
                                variant="primary"
                                className="next-btn"
                                onClick={handleNext}
                                disabled={createLoading || refreshLoading}
                            >
                                {currentStep === 3 ? (
                                    <>
                                        {createLoading || refreshLoading ? (
                                            <>
                                                {formData.id ? t('UPDATING') : t('CREATING')}
                                                <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>
                                            </>
                                        ) : (
                                            <>
                                                {formData.id ? t('UPDATE_DETAILS') : t('SAVE_DETAILS')}
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginLeft: '8px' }}>
                                                    <path d="M3.33203 8H11.9987M8.66536 4L12.194 7.5286C12.4543 7.78894 12.4543 8.21106 12.194 8.4714L8.66536 12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                                                </svg>
                                            </>
                                        )}
                                    </>
                                ) : t('NEXT')}
                            </Button>
                        </div>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default AddCustomerModal;